import type {
  AssessmentListParams,
  AssessmentListResponse,
  AssessmentStatusResponse,
} from "@/types";
import type {
  AssessmentCompanyDataOverride,
  AuditContextRequest,
} from "@vardic/shared/types.ts";
import { request, requestBlob } from "./request";

export interface CreateAssessmentResponse {
  assessmentId: string;
  status: "queued";
  estimatedTimeSeconds: number;
}

export async function listAssessments(
  params: AssessmentListParams = {},
): Promise<AssessmentListResponse> {
  const searchParams = new URLSearchParams();
  if (params.file_id !== undefined) searchParams.set("file_id", params.file_id);
  if (params.page !== undefined) searchParams.set("page", String(params.page));
  if (params.limit !== undefined) searchParams.set("limit", String(params.limit));

  const queryString = searchParams.toString();
  const path = queryString ? `/assessments?${queryString}` : "/assessments";
  return request<AssessmentListResponse>(path);
}

export async function getAssessment(
  assessmentId: string,
): Promise<AssessmentStatusResponse> {
  return request<AssessmentStatusResponse>(`/assessments/${assessmentId}`);
}

export async function downloadAssessmentPdf(
  assessmentId: string,
): Promise<Blob> {
  return requestBlob(`/assessments/${assessmentId}/pdf`);
}

export async function createAssessment(data?: {
  auditId?: string;
  teamId?: string;
  companyDataOverride?: AssessmentCompanyDataOverride;
  /**
   * Standalone (auditId-less) intake context — the exact declared
   * `AuditContextRequest` the user supplied. Required by the backend for
   * standalone creations; coupled runs may omit it (the linked audit's own
   * context is authoritative).
   */
  context?: AuditContextRequest;
}): Promise<CreateAssessmentResponse> {
  return request<CreateAssessmentResponse>("/assessments", {
    method: "POST",
    body: data ?? {},
    contentType: "json",
  });
}

export async function getCurrentAssessment(
  auditId?: string,
): Promise<AssessmentStatusResponse | null> {
  const path = auditId
    ? `/assessments/current?auditId=${encodeURIComponent(auditId)}`
    : "/assessments/current";
  return request<AssessmentStatusResponse | null>(path);
}
